// ================================================================
// 🔍 FILTER BAR - Barra de Filtros para Relatórios
// ================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import FilterDrawer from './FilterDrawer';
import ActiveFiltersTags from './ActiveFiltersTags';
import api from '../../services/api';

/**
 * 🔍 FilterBar - Barra de filtros com período e filtros de usuário
 * 
 * @param {Object} props
 * @param {string} props.period - Período selecionado ('7d', '30d', '3m', '6m', '1y', 'custom')
 * @param {Function} props.onPeriodChange - Callback ao mudar período
 * @param {Function} props.onApplyAdvancedFilters - Callback ao aplicar filtros avançados
 * @param {Function} props.onRefresh - Callback ao clicar em atualizar
 * @param {Function} props.onExport - Callback ao clicar em exportar
 * @param {boolean} props.loading - Estado de carregamento
 * @param {boolean} props.showExport - Mostrar botão de exportar
 * @param {Object} props.advancedFilters - Filtros avançados atuais
 * @param {Function} props.onUserFilterChange - Callback quando filtros de usuário mudam
 */
const FilterBar = ({ 
  period = '30d',
  onPeriodChange,
  onApplyAdvancedFilters,
  onRefresh,
  onExport,
  loading = false,
  showExport = true,
  advancedFilters = {},
  onUserFilterChange
}) => {
  const [showDrawer, setShowDrawer] = useState(false);
  
  // Estados para filtros de usuário
  const [userType, setUserType] = useState(advancedFilters?.user_type || '');
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(advancedFilters?.user_id || '');
  const [loadingUsers, setLoadingUsers] = useState(false);

  /**
   * Carrega usuários baseado no tipo selecionado
   */
  const loadUsersByType = useCallback(async (type) => {
    if (!type) {
      setAvailableUsers([]);
      setSelectedUser('');
      if (onUserFilterChange) {
        onUserFilterChange({ user_type: '', user_id: '' });
      }
      return;
    }

    setLoadingUsers(true);
    try {
      const response = await api.get(`/api/reports/users-by-type?type=${type}`);
      
      if (response.data.success) {
        setAvailableUsers(response.data.data);
        console.log(`✅ [FilterBar] ${response.data.data.length} usuários REAIS carregados:`, response.data.data);
      } else {
        setAvailableUsers([]);
      }
      setSelectedUser('');
    } catch (err) {
      console.error('❌ Erro ao carregar usuários:', err);
      setAvailableUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  /**
   * Handle mudança de tipo de usuário
   */
  const handleUserTypeChange = (e) => {
    const newType = e.target.value;
    setUserType(newType);
    if (newType) {
      loadUsersByType(newType);
      // Filtra imediatamente quando tipo é selecionado
      if (onUserFilterChange) {
        onUserFilterChange({ user_type: newType, user_id: '' });
      }
    } else {
      // Se desmarcar o tipo, remove tudo
      setSelectedUser('');
      setAvailableUsers([]);
      if (onUserFilterChange) {
        onUserFilterChange({ user_type: '', user_id: '' });
      }
    }
  };

  /**
   * Handle mudança de usuário específico
   */
  const handleUserChange = (e) => {
    const userId = e.target.value;
    setSelectedUser(userId);
    if (onUserFilterChange) {
      onUserFilterChange({ user_type: userType, user_id: userId || '' });
    }
  };

  /**
   * Conta filtros avançados ativos
   */
  const getActiveFiltersCount = () => {
    let count = 0;
    if (advancedFilters.product_id) count++;
    if (advancedFilters.payment_method) count++;
    if (advancedFilters.sale_status) count++;
    if (advancedFilters.region) count++;
    if (advancedFilters.customDateRange?.start && advancedFilters.customDateRange?.end) count++;
    return count;
  };

  const activeFiltersCount = getActiveFiltersCount();

  return (
    <>
      <Row className="g-3 align-items-end" style={{ marginBottom: '24px' }}>
        {/* Filtro de Período */}
        <Col xs={12} sm={6} md={3}>
          <Form.Group>
            <Form.Label style={{ 
              fontSize: '14px', 
              fontWeight: '600', 
              color: '#6c757d',
              marginBottom: '8px'
            }}>
              📅 Período
            </Form.Label>
            <Form.Select
              value={period}
              onChange={(e) => onPeriodChange && onPeriodChange(e.target.value)}
              disabled={loading}
              style={{
                borderRadius: '8px',
                border: '2px solid #e9ecef',
                padding: '10px 12px',
                fontSize: '14px'
              }}
            >
              <option value="7d">Últimos 7 dias</option>
              <option value="30d">Últimos 30 dias</option>
              <option value="3m">Últimos 3 meses</option>
              <option value="6m">Últimos 6 meses</option>
              <option value="1y">Último ano</option>
              <option value="custom">Personalizado</option>
            </Form.Select>
          </Form.Group>
        </Col>

        {/* Filtro de Tipo de Usuário */}
        <Col xs={12} sm={6} md={3}>
          <Form.Group>
            <Form.Label style={{ 
              fontSize: '14px', 
              fontWeight: '600', 
              color: '#6c757d',
              marginBottom: '8px'
            }}>
              <Icon icon="mdi:account-group" style={{ marginRight: '6px', color: '#007bff' }} />
              Tipo de Usuário
            </Form.Label>
            <Form.Select
              value={userType}
              onChange={handleUserTypeChange}
              disabled={loading}
              style={{
                borderRadius: '8px',
                border: '2px solid #e9ecef',
                padding: '10px 12px',
                fontSize: '14px'
              }}
            >
              <option value="">Todos os tipos</option>
              <option value="seller">Vendedor</option>
              <option value="direct_affiliate">Afiliado Direto</option>
              <option value="indirect_affiliate">Afiliado Indireto</option>
            </Form.Select>
          </Form.Group>
        </Col>

        {/* Filtro de Usuário Específico */}
        <Col xs={12} sm={6} md={3}>
          <Form.Group>
            <Form.Label style={{ 
              fontSize: '14px', 
              fontWeight: '600', 
              color: '#6c757d',
              marginBottom: '8px'
            }}>
              <Icon icon="mdi:account-search" style={{ marginRight: '6px', color: '#28a745' }} />
              Usuário
            </Form.Label>
            <Form.Select
              value={selectedUser}
              onChange={handleUserChange}
              disabled={!userType || loadingUsers || loading}
              style={{
                borderRadius: '8px',
                border: '2px solid #e9ecef',
                padding: '10px 12px',
                fontSize: '14px'
              }}
            >
              <option value="">
                {!userType ? 'Selecione o tipo primeiro' : loadingUsers ? 'Carregando...' : 'Todos os usuários'}
              </option>
              {availableUsers.map(user => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

      </Row>

      {/* Tags de Filtros Ativos */}
      <ActiveFiltersTags
        filters={{
          user_type: userType || undefined,
          user_id: selectedUser || undefined,
          ...advancedFilters
        }}
        onRemoveFilter={(key) => {
          if (key === 'user_type') {
            // Remover tipo remove tudo
            setUserType('');
            setSelectedUser('');
            setAvailableUsers([]);
            if (onUserFilterChange) {
              onUserFilterChange({ user_type: '', user_id: '' });
            }
          } else if (key === 'user_id') {
            // Remover usuário mantém o tipo
            setSelectedUser('');
            if (onUserFilterChange) {
              onUserFilterChange({ user_type: userType, user_id: '' });
            }
          } else {
            // Outros filtros avançados
            if (onApplyAdvancedFilters) {
              const newFilters = { ...advancedFilters };
              delete newFilters[key];
              onApplyAdvancedFilters(newFilters);
            }
          }
        }}
        availableUsers={availableUsers}
      />

      {/* Drawer de Filtros Avançados */}
      <FilterDrawer
        show={showDrawer}
        onHide={() => setShowDrawer(false)}
        onApply={(filters) => {
          setShowDrawer(false);
          onApplyAdvancedFilters && onApplyAdvancedFilters(filters);
        }}
        currentFilters={advancedFilters}
      />
    </>
  );
};

export default FilterBar;

